import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Tag } from '../models/tag.entity'
import { TagsService } from '../tags.service'
import { TagsRepository } from '../tags.repository'

interface TagDetailState {
  tag?: Tag
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const TAG_DETAIL_INITIAL_STATE: TagDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class TagDetailStore {
  constructor(
    private tagsService: TagsService,
    private tagsRepository: TagsRepository,
  ) {}

  private state = signal<TagDetailState>(TAG_DETAIL_INITIAL_STATE)

  // Selectors
  readonly tag = computed(() => this.state().tag)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(tagId?: string): Promise<void> {
    if (!tagId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.tagsService.read$(tagId).subscribe({
        next: async (data) => {
          resolve()
          this.setData(
            data,
            this.state().tag?._id ? this.state().tag?._id !== data._id : false,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(tag: Tag, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.tagsService.upsert$(tag).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, false)
          if (notify === true) {
            this.tagsRepository.notify({ code: 'TAG_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(tag: Tag, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      tag,
      isModified: isModified ?? this.state().isModified,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
